<template>
  <v-app id="billing-settings">
    <div class="wrap-billing-settings">
      <v-layout class="wrap xs12">
        <v-layout class="wrap">
          <v-flex class="mb-4 mr-4">
            <div class="billing-top-header">
              <div class="caption mb-2">
                <span 
                  @click="$router.push('/settings')"
                  class="pointer primary--text text-uppercase"
                >Settings</span>
                <span class="px-2 grey--text">/</span>
                <span 
                  class="grey--text text-uppercase"
                >Client Portal</span>
              </div>
              <h2 class="headline">Client Portal</h2>
            </div>
          </v-flex>
          <v-flex>
            <v-layout class="wrap justify-end">
              <v-menu 
                v-if="permissions.includes('view_onboarding_form')"
                offset-y bottom left
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="mb-3"
                    elevation="0"
                    v-on="on"
                    small
                    :disabled="loadingForm"
                  ><v-icon small left>link</v-icon> Client Onboarding <v-icon>arrow_drop_down</v-icon></v-btn>
                </template>
                <v-list class="py-0">
                  <v-list-item @click="openLinkInNewTab(clientFromLink)">
                    <v-list-item-title>
                      <v-icon color="primary" class="mr-2">open_in_new</v-icon>
                      View in a browser
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="copyLink(clientFromLink)">
                    <v-list-item-title>
                      <v-icon color="primary" class="mr-2">content_copy</v-icon>
                      Copy the link
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-menu 
                offset-y bottom left
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="ml-2 mb-2"
                    elevation="0"
                    v-on="on"
                    small
                    :disabled="loadingForm"
                  ><v-icon small left>link</v-icon> Client Login <v-icon>arrow_drop_down</v-icon></v-btn>
                </template>
                <v-list class="py-0">
                  <v-list-item @click="openLinkInNewTab(clientLoginLink)">
                    <v-list-item-title>
                      <v-icon color="primary" class="mr-2">open_in_new</v-icon>
                      View in a browser
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="copyLink(clientLoginLink)">
                    <v-list-item-title>
                      <v-icon color="primary" class="mr-2">content_copy</v-icon>
                      Copy the link
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-layout>
    </div>

    <v-card class="mt-6">
      <div class="title pt-6 px-6 pb-3">Client Dashboard</div>
      <v-divider></v-divider>
      <v-progress-linear
        :indeterminate="loading"
        class="ma-0"
        :class="{ 'no-opacity': !loading }"
        style="height: 2px;"
      ></v-progress-linear>
      <div class="px-6 py-4 mt-4">
        <v-layout class="wrap">
          <v-flex class="xs12 sm12 md4 lg3">
            <div class="text-subtitle-2 mb-3 mt-2">Show Company Address</div>
          </v-flex>
          <v-flex class="xs12 sm12 md8 lg9">
            <v-switch
              true-value="1"
              false-value="0"
              class="mt-0 mb-4"
              hide-details
              color="info"
              v-model="editClientPortalData.show_company_address_client_portal"
            ></v-switch>
            <div class="text-body-2">Let clients see your address on the client
              portal dashboard. Example: If you accept check payments via regular mail.
            </div>
            <div
              @click="$router.push('branding_settings?edit=true')"
              class="text-body-2 primary--text pointer"
            >Edit Company Address</div>
          </v-flex>
        </v-layout>
      </div>
      <div class="px-6 py-4 mt-4">
        <v-layout class="wrap">
          <v-flex class="xs12 sm12 md4 lg3">
            <div class="text-subtitle-2 mb-3 mt-2">Show Phone Number</div>
          </v-flex>
          <v-flex class="xs12 sm12 md8 lg9">
            <v-switch
              true-value="1"
              false-value="0"
              class="mt-0 mb-4"
              hide-details
              color="info"
              v-model="editClientPortalData.show_company_phone_client_portal"
            ></v-switch>
            <div class="text-body-2">Let clients see your contact phone
              number on the client portal dashboard.
            </div>
            <div
              @click="$router.push('branding_settings?edit=true')"
              class="text-body-2 primary--text pointer"
            >Edit Phone Number</div>
            <v-checkbox
              class="mx-0 mb-0 mt-4"
              true-value="1"
              false-value="0"
              hide-details
              v-model="editClientPortalData.can_text_company_phone_client_portal"
            >
              <template v-slot:label>
                  <span class="text-body-2">Client can text the number</span>
                </template>
            </v-checkbox>
            <v-checkbox
              class="mx-0 mb-0 mt-2"
              true-value="1"
              false-value="0"
              hide-details
              v-model="editClientPortalData.can_call_company_phone_client_portal"
            >
              <template v-slot:label>
                  <span class="text-body-2">Client can call the number</span>
                </template>
            </v-checkbox>
          </v-flex>
        </v-layout>
      </div>
      <div class="px-6 py-4 mt-4">
        <v-layout class="wrap">
          <v-flex class="xs12 sm12 md4 lg3">
            <div class="text-subtitle-2 mb-3 mt-2">Show Email Address</div>
          </v-flex>
          <v-flex class="xs12 sm12 md8 lg9">
            <v-switch
              true-value="1"
              false-value="0"
              class="mt-0 mb-4"
              hide-details
              color="info"
              v-model="editClientPortalData.show_company_email_client_portal"
            ></v-switch>
            <div class="text-body-2">Let clients see your contact email on the client portal dashboard. 
            </div>
            <div
              class="text-body-2 primary--text pointer"
              @click="$router.push('branding_settings?edit=true')"  
            >Edit Email</div>
          </v-flex>
        </v-layout>
      </div>
      <div class="pt-3 px-6 pb-5">
        <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
          <span class="caption">{{ serverValidationMessage }}</span>
        </v-alert>
        <v-btn
          v-if="!loading"
          @click="editClientPortalSetting"
          :loading="saveLoading"
          :disabled="saveLoading"
          class="mr-0 mt-0"
          color="primary"
        >
          Save Changes
        </v-btn>
      </div>
    </v-card>
  </v-app>
</template>

<script>
import ApiService from "@/common/api.service"
import { mapGetters, mapActions } from "vuex"

export default {
  name: "FieldTechAppSettings",
  data() {
    return {
      no_data: [null, '', undefined, 'null'],
      loading: false,
      organization_settings: {},
      editClientPortalData: {},
      saveLoading: false,
      clientFromLink: '',
      loadingForm: false,
      serverValidation: true,
      serverValidationMessage: 'Oops something went wrong...'
    }
  },
  mounted() {
    this.initialize()
    this.getOngoardingForm()
  },
  methods: {
    ...mapActions(['vEmit']),
    initialize () {
      this.loading = true
      ApiService.get('/organization_settings')
        .then(response => {
          let organization_settings = {}
          response.data.organization_settings.forEach(i => {
            organization_settings[i.key] = i.value
          })
          organization_settings.show_logo_onboarding = Boolean(Number(organization_settings.show_logo_onboarding))
          organization_settings.show_name_onboarding = Boolean(Number(organization_settings.show_name_onboarding))
          organization_settings.show_payroll_filed_tech = Boolean(Number(organization_settings.show_payroll_filed_tech))
          this.organization_settings = organization_settings
          this.editClientPortalData = {...this.organization_settings}
          delete this.editClientPortalData.logo
          this.loading = false
        })
        .catch(error => {
          console.log(error)
          this.loading = false
        });
    },
    getOngoardingForm () {
      this.loadingForm = true
      ApiService.get(
        "/client_on_boarding_setup/organization_form/client_on_boarding_registration_form"
      )
        .then(response => {
          this.clientFromLink = response.data.client_registration_link;
          this.loadingForm = false
        })
        .catch(error => {
          if (error.response.status == 500) {
            this.error_code = error.response.status;
          }
          this.loadingForm = false
        });
    },
    editClientPortalSetting () {
      this.resetServerValidation()

      let body = { organization_settings: []}
      for (let [key, value] of Object.entries(this.editClientPortalData)) {
        if(!this.no_data.includes(value)) {
          body.organization_settings.push({key: key, value: value})
        }
      }

      this.saveLoading = true
      this.resetServerValidation()

      ApiService.put('/organization_settings', body)
        .then(response => {
          this.saveLoading = false
          this.vEmit({ 
            name: "notification", 
            data: {
              status: 'success', 
              text: 'Client Portal Settings has been successfully edited.'
            }
          })
          this.initialize();
        })
        .catch(({ response }) => {
          this.saveLoading = false
          this.serverValidation = false
          if(response.data.error){
              this.serverValidationMessage = this.titleCase(response.data.error)
          }
        })
    },
    openLinkInNewTab (link) {
      window.open(link, "_blank");
    },
    copyLink (link) {
      try {
        navigator.clipboard.writeText(link)
        this.vEmit({ 
          name: "notification", 
          data: {
            status: "success",
            text: "Link has been successfully copied to clipboard."
          }
        });
      } catch {
        this.vEmit({ 
          name: "notification",
          data: {
            status: "error",
            text: "Oops something went wrong..."
          }
        });
      }
    },
    titleCase(str){
      return _.startCase(str.replace(/_/g, ' '));
    },
    resetServerValidation() {
      this.serverValidation = true
      this.serverValidationMessage = 'Oops something went wrong...'
    }
  },
  computed: {
    ...mapGetters(["permissions"]),
    clientLoginLink () {
      let output = this.clientFromLink
      if (typeof output === 'string' && output !== '') {
        let parts = output.split('/')
        let slug = parts[parts.length - 2]
        let base = parts.slice(0, -2).join('/')
        output = [base, 'login', slug].join('/')
      }
      return output
    }
  }
}
</script>
